import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { WindowState } from '@progress/kendo-angular-dialog';
import _ from 'lodash';
import {
   CreateWindow,
   MaximizeWindow,
   HideWindow,
   RemoveWindow,
   RestoreWindow,
   SetWindowDraggedOrResized,
   SetWindowHeight,
   SetWindowIndex,
   SetWindowLeft,
   SetWindowTop,
   SetWindowWidth,
   ToggleWindow,
} from './samweb-window.actions';

export class WindowTypes {
   public static readonly Directory = 'directory';
   public static readonly Email = 'email';
   public static readonly Chat = 'chat';
}
export declare type WindowType = 'directory' | 'email' | 'chat';

export interface SamwebWindowInstances {
   [id: string]: SamwebWindowInstance;
}
export interface SamwebWindowStateModel {
   [type: string]: SamwebWindowInstances;
}

export interface SamwebWindowInstance {
   id: string;
   open: boolean;
   windowState: WindowState;
   isDraggedOrResized: boolean;
   top: number;
   left: number;
   height: number;
   width: number;
   zIndex: number;
}

export function DefaultEmailBehaviourInstance(): SamwebWindowInstance {
   return {
      id: null,
      open: true,
      windowState: 'default',
      isDraggedOrResized: false,
      top: null,
      left: null,
      height: null,
      width: null,
      zIndex: null,
   };
}

class WindowStartupDefault {
   static Width = 800;
   static Height = 600;
   static zIndex = 10000;
}

class WindowStartupValue {
   public Width: number;
   public Height: number;
   public get Top(): number {
      return (window.innerHeight - this.Height) / 2;
   }
   public get Left(): number {
      return (window.innerWidth - this.Width) / 2;
   }
   public zIndex: number;

   constructor(width: number, height: number) {
      this.Width = width;
      this.Height = height;
      this.zIndex = WindowStartupDefault.zIndex;
   }
}

class WindowStartupValues {
   static Default: WindowStartupValue = new WindowStartupValue(WindowStartupDefault.Width, WindowStartupDefault.Height);
   static Email: WindowStartupValue = new WindowStartupValue(WindowStartupDefault.Width, WindowStartupDefault.Height);
   static Chat: WindowStartupValue = new WindowStartupValue(WindowStartupDefault.Width, WindowStartupDefault.Height);
   static DirectoryEditing: WindowStartupValue = new WindowStartupValue(600, 600);

   constructor() {}

   static get(type: WindowType): WindowStartupValue {
      switch (type) {
         case WindowTypes.Directory:
            return this.DirectoryEditing;
         case WindowTypes.Email:
            return this.Email;
         case WindowTypes.Chat:
            return this.Chat;
         default:
            return this.Default;
      }
   }

   static setWidth(type: WindowType, value: number) {
      switch (type) {
         case WindowTypes.Directory:
            this.DirectoryEditing.Width = value;
            break;
         case WindowTypes.Email:
            this.Email.Width = value;
            break;
         case WindowTypes.Chat:
            this.Chat.Width = value;
            break;
         default:
            this.Default.Width = value;
            break;
      }
   }

   static setHeight(type: WindowType, value: number) {
      switch (type) {
         case WindowTypes.Directory:
            this.DirectoryEditing.Height = value;
            break;
         case WindowTypes.Email:
            this.Email.Height = value;
            break;
         case WindowTypes.Chat:
            this.Chat.Height = value;
            break;
         default:
            this.Default.Width = value;
            break;
      }
   }
}

@State<SamwebWindowStateModel>({
   name: 'samwebWindowState',
   defaults: {},
})
@Injectable()
export class SamwebWindowState {
   private static getSpecificBehaviourStateOrDefault(state: SamwebWindowStateModel, type: WindowType, id: string): SamwebWindowInstance {
      const currentInstances: SamwebWindowInstances = state[type];
      if (!currentInstances) {
         return DefaultEmailBehaviourInstance();
      }
      const currentInstance: SamwebWindowInstance = currentInstances[id];
      if (!currentInstance) {
         return DefaultEmailBehaviourInstance();
      }
      return currentInstance;
   }

   private static augmentState(state: SamwebWindowStateModel, type: WindowType, id: string) {
      if (_.isNil(state[type])) {
         state[type] = {};
      }
      if (_.isNil(state[type][id])) {
         state[type][id] = null;
      }
   }

   private static isStateCreated(state: SamwebWindowStateModel, type: WindowType, id: string) {
      if (_.isNil(state[type])) {
         return false;
      }
      if (_.isNil(state[type][id])) {
         return false;
      }
      return true;
   }

   @Selector([SamwebWindowState])
   public static getWindowState(state: SamwebWindowStateModel): (type: WindowType, id: string) => WindowState {
      return (type: WindowType, id: string) => {
         return SamwebWindowState.isStateCreated(state, type, id) && !_.isNil(state[type][id]?.windowState) ? state[type][id].windowState : 'default';
      };
   }

   @Selector([SamwebWindowState])
   public static getHighestIndex(state: SamwebWindowStateModel) {
      const result: number = _(state)
         .flatMap((emailState) =>
            _(emailState)
               .map((x) => x.zIndex)
               .max()
         )
         .max();
      return !_.isUndefined(result) ? result : WindowStartupDefault.zIndex;
   }

   @Selector([SamwebWindowState])
   public static isWindowDraggedOrResized(state: SamwebWindowStateModel) {
      const result: boolean = _(state)
         .flatMap((emailState) =>
            _(emailState)
               .map((x) => x.isDraggedOrResized)
               .max()
         )
         .max();
      return !_.isUndefined(result) ? result : false;
   }

   @Selector([SamwebWindowState])
   public static isOpen(state: SamwebWindowStateModel): (type: WindowType, id: string) => boolean {
      return (type: WindowType, id: string) => {
         return SamwebWindowState.isStateCreated(state, type, id) ? state[type][id]?.open : true;
      };
   }

   @Selector([SamwebWindowState])
   public static getTopValue(state: SamwebWindowStateModel): (type: WindowType, id: string) => number {
      return (type: WindowType, id: string) => {
         return SamwebWindowState.isStateCreated(state, type, id) && !_.isNil(state[type][id]?.top) ? state[type][id].top : WindowStartupValues.get(type).Top;
      };
   }

   @Selector([SamwebWindowState])
   public static getLeftValue(state: SamwebWindowStateModel): (type: WindowType, id: string) => number {
      return (type: WindowType, id: string) => {
         return SamwebWindowState.isStateCreated(state, type, id) && !_.isNil(state[type][id]?.left)
            ? state[type][id].left
            : WindowStartupValues.get(type).Left;
      };
   }

   @Selector([SamwebWindowState])
   public static getHeightValue(state: SamwebWindowStateModel): (type: WindowType, id: string) => number {
      return (type: WindowType, id: string) => {
         return SamwebWindowState.isStateCreated(state, type, id) && !_.isNil(state[type][id]?.height)
            ? state[type][id].height
            : WindowStartupValues.get(type).Height;
      };
   }

   @Selector([SamwebWindowState])
   public static getWidthValue(state: SamwebWindowStateModel): (type: WindowType, id: string) => number {
      return (type: WindowType, id: string) => {
         return SamwebWindowState.isStateCreated(state, type, id) && !_.isNil(state[type][id]?.width)
            ? state[type][id].width
            : WindowStartupValues.get(type).Width;
      };
   }

   @Selector([SamwebWindowState])
   public static getIndexValue(state: SamwebWindowStateModel): (type: WindowType, id: string) => number {
      return (type: WindowType, id: string) => {
         return SamwebWindowState.isStateCreated(state, type, id) && !_.isNil(state[type][id]?.zIndex)
            ? state[type][id].zIndex
            : WindowStartupValues.get(type).zIndex;
      };
   }

   @Action(ToggleWindow)
   @ImmutableContext()
   public toggleWindow(ctx: StateContext<SamwebWindowStateModel>, action: ToggleWindow) {
      return ctx.setState((state: SamwebWindowStateModel) => {
         const behaviour = SamwebWindowState.getSpecificBehaviourStateOrDefault(state, action.type, action.id);
         behaviour.open = !behaviour.open;
         behaviour.windowState = behaviour.open ? 'default' : 'minimized';
         state[action.type][action.id] = behaviour;
         return state;
      });
   }

   @Action(RestoreWindow)
   @ImmutableContext()
   public restoreWindow(ctx: StateContext<SamwebWindowStateModel>, action: RestoreWindow) {
      return ctx.setState((state: SamwebWindowStateModel) => {
         const behaviour = SamwebWindowState.getSpecificBehaviourStateOrDefault(state, action.type, action.id);
         behaviour.open = true;
         behaviour.windowState = 'default';
         state[action.type][action.id] = behaviour;
         return state;
      });
   }

   @Action(MaximizeWindow)
   @ImmutableContext()
   public maximizeWindow(ctx: StateContext<SamwebWindowStateModel>, action: MaximizeWindow) {
      return ctx.setState((state: SamwebWindowStateModel) => {
         const behaviour = SamwebWindowState.getSpecificBehaviourStateOrDefault(state, action.type, action.id);
         behaviour.open = true;
         behaviour.windowState = 'maximized';
         state[action.type][action.id] = behaviour;
         return state;
      });
   }

   @Action(HideWindow)
   @ImmutableContext()
   public hideWindow(ctx: StateContext<SamwebWindowStateModel>, action: HideWindow) {
      return ctx.setState((state: SamwebWindowStateModel) => {
         const behaviour = SamwebWindowState.getSpecificBehaviourStateOrDefault(state, action.type, action.id);
         behaviour.open = false;
         state[action.type][action.id] = behaviour;
         return state;
      });
   }

   @Action(SetWindowIndex)
   @ImmutableContext()
   public setWindowIndex(ctx: StateContext<SamwebWindowStateModel>, action: SetWindowIndex) {
      return ctx.setState((state: SamwebWindowStateModel) => {
         const behaviour = SamwebWindowState.getSpecificBehaviourStateOrDefault(state, action.type, action.id);
         behaviour.zIndex = action.zIndex;
         state[action.type][action.id] = behaviour;
         return state;
      });
   }

   @Action(SetWindowTop)
   @ImmutableContext()
   public setWindowTop(ctx: StateContext<SamwebWindowStateModel>, action: SetWindowTop) {
      return ctx.setState((state: SamwebWindowStateModel) => {
         const behaviour = SamwebWindowState.getSpecificBehaviourStateOrDefault(state, action.type, action.id);
         behaviour.top = action.top;
         if (action.top < 0 || behaviour.height > window.innerHeight) {
            behaviour.top = 0;
         }
         if (action.top > window.innerHeight - behaviour.height) {
            behaviour.top = window.innerHeight - behaviour.height;
         }
         if (behaviour.height > window.innerHeight) {
            behaviour.top = 0;
            behaviour.height = window.innerHeight;
         }
         state[action.type][action.id] = behaviour;
         return state;
      });
   }

   @Action(SetWindowLeft)
   @ImmutableContext()
   public setWindowLeft(ctx: StateContext<SamwebWindowStateModel>, action: SetWindowLeft) {
      return ctx.setState((state: SamwebWindowStateModel) => {
         const behaviour = SamwebWindowState.getSpecificBehaviourStateOrDefault(state, action.type, action.id);
         behaviour.left = action.left;
         if (action.left < 0) {
            behaviour.left = 0;
         }
         if (action.left > window.innerWidth - behaviour.width) {
            behaviour.left = window.innerWidth - behaviour.width;
         }
         if (behaviour.width > window.innerWidth) {
            behaviour.top = 0;
            behaviour.width = window.innerWidth;
         }
         state[action.type][action.id] = behaviour;
         return state;
      });
   }

   @Action(SetWindowHeight)
   @ImmutableContext()
   public setWindowHeight(ctx: StateContext<SamwebWindowStateModel>, action: SetWindowHeight) {
      return ctx.setState((state: SamwebWindowStateModel) => {
         const behaviour = SamwebWindowState.getSpecificBehaviourStateOrDefault(state, action.type, action.id);
         behaviour.height = action.height;
         if (action.height < 0) {
            behaviour.height = 0;
         }
         if (action.height > window.innerHeight - behaviour.top) {
            behaviour.height = window.innerHeight - behaviour.top;
         }
         if (behaviour.height > window.innerHeight) {
            behaviour.top = 0;
            behaviour.height = window.innerHeight;
         }
         // WindowStartupValues.get(action.type).Height = behaviour.height;

         WindowStartupValues.setHeight(action.type, behaviour.height);
         state[action.type][action.id] = behaviour;
         return state;
      });
   }

   @Action(SetWindowWidth)
   @ImmutableContext()
   public setWindowWidth(ctx: StateContext<SamwebWindowStateModel>, action: SetWindowWidth) {
      return ctx.setState((state: SamwebWindowStateModel) => {
         const behaviour = SamwebWindowState.getSpecificBehaviourStateOrDefault(state, action.type, action.id);
         behaviour.width = action.width;
         if (action.width < 0) {
            behaviour.width = 0;
         }
         if (action.width > window.innerWidth - behaviour.left) {
            behaviour.width = window.innerWidth - behaviour.left;
         }
         if (behaviour.width > window.innerWidth) {
            behaviour.top = 0;
            behaviour.width = window.innerWidth;
         }
         // WindowStartupValues.get(action.type).Width = behaviour.width;
         WindowStartupValues.setWidth(action.type, behaviour.width);
         state[action.type][action.id] = behaviour;
         return state;
      });
   }

   @Action(SetWindowDraggedOrResized)
   @ImmutableContext()
   public setWindowDraggedOrResized(ctx: StateContext<SamwebWindowStateModel>, action: SetWindowDraggedOrResized) {
      return ctx.setState((state: SamwebWindowStateModel) => {
         const behaviour = SamwebWindowState.getSpecificBehaviourStateOrDefault(state, action.type, action.id);
         behaviour.isDraggedOrResized = action.isEdited;
         state[action.type][action.id] = behaviour;
         return state;
      });
   }

   @Action(CreateWindow)
   @ImmutableContext()
   public createWindow(ctx: StateContext<SamwebWindowStateModel>, action: CreateWindow) {
      return ctx.setState((state: SamwebWindowStateModel) => {
         const behaviour = SamwebWindowState.getSpecificBehaviourStateOrDefault(state, action.type, action.id);
         behaviour.id = action.id;
         SamwebWindowState.augmentState(state, action.type, action.id);
         state[action.type][action.id] = behaviour;
         return state;
      });
   }

   @Action(RemoveWindow)
   @ImmutableContext()
   public removeWindow(ctx: StateContext<SamwebWindowStateModel>, action: RemoveWindow) {
      return ctx.setState((state: SamwebWindowStateModel) => {
         SamwebWindowState.augmentState(state, action.type, action.id);
         delete state[action.type][action.id];
         return state;
      });
   }
}
